/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Tooltip, Typography } from '@mui/material';
import {
  select,
  scaleBand,
  scaleSequentialLog,
  format,
  utcFormat,
  pointer,
  axisBottom,
  axisLeft,
  scaleLinear,
} from 'd3';
import type { Selection, ScaleBand } from 'd3';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { densityToBirdTAM } from '../utils/birdTAM';
import {
  birdTAMColorScale,
  interpolateBioRadColorScale,
} from '../utils/colorScales';
import { useFlySafeTranslation } from '../utils/i18n';
import { getPlotMargins } from '../utils/plotLayout';
import {
  DAY_MS,
  HOUR_MS,
  FIVE_MIN_MS,
  buildContinuousTimes,
  closestTimeBand,
} from '../utils/timeUtils';
import type { FlySafeRow, Mode } from '../utils/types';
import {
  getViewBoxWH,
  pickIconColor,
  pickWindSymbol,
} from '../utils/windsymbols';

interface HeatMapProps {
  timeWindow?: [Date, Date];
  mode?: Mode;
  rows?: FlySafeRow[] | null;
  mapTime?: Date | null;
  onTimeSelect?: (time: Date) => void;
  stationId?: string;
}

function cleanup(node: HTMLDivElement | null): void {
  if (node) {
    select(node).selectAll('*').remove();
  }
}

const BASE_WIDTH = 1500;
const BASE_HEIGHT = 800;
const ASPECT = BASE_HEIGHT / BASE_WIDTH; // keep the same proportions as before
const MIN_WIDTH = 320;
const FEET_STEP = 1000;

const HeatMap = (props: HeatMapProps): ReactNode => {
  const { rows, timeWindow, mode, mapTime, onTimeSelect, stationId } = props;
  const { t } = useFlySafeTranslation();

  // Stable ref so D3 handlers always see the latest callback
  const onTimeSelectRefStable = useRef(onTimeSelect);
  onTimeSelectRefStable.current = onTimeSelect;

  // Outer container gives the measurable wifth
  const containerRef = useRef<HTMLDivElement>(null);
  // Mount SVG here
  const svgHostRef = useRef<HTMLDivElement>(null);

  const [containerWidth, setContainerWidth] = useState(0);

  // Observe width so SVG redraws responsively
  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return (): void => {};
    }

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const ro = new ResizeObserver((entries) => {
      const wd = Math.floor(entries[0]?.contentRect?.width ?? 0);
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => setContainerWidth(wd), 150);
    });

    ro.observe(el);
    return (): void => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      ro.disconnect();
    };
  }, []);

  // Ref to hold chart internals needed for the indicator line
  const chartRef = useRef<{
    gPlot: Selection<SVGGElement, unknown, null, undefined>;
    xRange: ScaleBand<Date>;
    bw: number;
    times: Date[];
    innerHeight: number;
  } | null>(null);

  useEffect(() => {
    const node = svgHostRef.current;
    if (!node) {
      return (): void => {};
    }

    const width = Math.max(MIN_WIDTH, containerWidth);
    if (!Number.isFinite(width) || width <= 0) {
      return (): void => {};
    }

    const height = Math.min(Math.max(160, Math.round(width * ASPECT)), 300);

    // Scale elements a bit for smaller or larger widths
    // Note: Make this more responsive in future iterations
    const uiScale = Math.max(0.6, Math.min(1.2, width / BASE_WIDTH));
    const TICK_FONT = Math.max(10, Math.round(16 * uiScale));
    const TIP_FONT = Math.max(10, Math.round(12 * uiScale));
    const EMPTY_FONT = TIP_FONT;

    cleanup(node);

    const margin = getPlotMargins(uiScale);

    const innerWidth = Math.max(1, width - margin.left - margin.right);
    const innerHeight = Math.max(1, height - margin.top - margin.bottom);

    const svg = select(node)
      .append('svg')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('display', 'block')
      .style('width', '100%')
      .style('height', 'auto')
      .style('touch-action', 'none');

    if (!rows || rows.length === 0) {
      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', EMPTY_FONT)
        .attr('fill', '#64748b')
        .text('No data');
      return (): void => cleanup(node);
    }

    let data: FlySafeRow[] = rows
      .map((curr) => ({
        datetime: new Date(curr.datetime),
        height: curr.height,
        dens: curr.dens ?? NaN,
        ff: curr.ff ?? NaN,
        dd: curr.dd ?? NaN,
      }))
      .filter(
        (dataPoint) =>
          Number.isFinite(dataPoint.height) &&
          !Number.isNaN(dataPoint.datetime.getTime()),
      );

    if (timeWindow?.[0] && timeWindow?.[1]) {
      const [t0, t1] = timeWindow;
      data = data.filter(
        (dataPoint) => dataPoint.datetime >= t0 && dataPoint.datetime <= t1,
      );
    }

    if (data.length === 0) {
      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', EMPTY_FONT)
        .attr('fill', '#64748b')
        .text('No data in range');
      return (): void => cleanup(node);
    }

    const heights = Array.from(
      new Set(data.map((dataPoint) => dataPoint.height)),
    ).sort((valA, valB) => valA - valB);

    // Determine time span
    const timestamps = data
      .map((dt) => dt.datetime.getTime())
      .sort((valA, valB) => valA - valB);

    const spanMs = timestamps.at(-1)! - timestamps[0]!;
    const multiDay = spanMs > DAY_MS;

    // If more than one day, downsample
    if (multiDay) {
      const firstPerHourHeight = new Map<string, FlySafeRow>();

      for (const row of data) {
        const hourKey = Date.UTC(
          row.datetime.getUTCFullYear(),
          row.datetime.getUTCMonth(),
          row.datetime.getUTCDate(),
          row.datetime.getUTCHours(),
        );

        const key = `${hourKey}|${row.height}`;

        if (!firstPerHourHeight.has(key)) {
          firstPerHourHeight.set(key, row);
        }
      }
      data = Array.from(firstPerHourHeight.values());
    }

    // Compute times: use continuous axis when timeWindow is available
    const stepMs = multiDay ? HOUR_MS : FIVE_MIN_MS;
    const times =
      timeWindow?.[0] && timeWindow?.[1]
        ? buildContinuousTimes(timeWindow[0], timeWindow[1], stepMs)
        : Array.from(
            new Set(data.map((dataPoint) => dataPoint.datetime.getTime())),
          )
            .sort((valA, valB) => valA - valB)
            .map((ms) => new Date(ms));

    const xRange = scaleBand<Date>().domain(times).range([0, innerWidth]);
    const yRange = scaleBand<number>().domain(heights).range([innerHeight, 0]);

    const yLinear = scaleLinear()
      .domain([0, Math.max(...heights)])
      .range([innerHeight, 0]);

    const bw = Math.max(1, xRange.bandwidth());
    const bh = Math.max(1, yRange.bandwidth());

    const bioRad = scaleSequentialLog(interpolateBioRadColorScale).domain([
      0.5, 1000,
    ]);

    const byKey = new Map<string, FlySafeRow>();
    for (const dat of data) {
      byKey.set(`${dat.datetime.getTime()}|${dat.height}`, dat);
    }

    const cellFill = (row?: FlySafeRow): string => {
      if (!row?.dens || !Number.isFinite(row.dens)) {
        return '#e2e8f0';
      }
      return mode === 'birdtam'
        ? birdTAMColorScale(densityToBirdTAM(row.dens))
        : bioRad(Math.max(row.dens, 1e-6));
    };

    const cells: {
      t: Date;
      height: number;
      row?: FlySafeRow;
      fill: string;
    }[] = [];
    for (const t of times) {
      for (const height of heights) {
        const row = byKey.get(`${t.getTime()}|${height}`);
        cells.push({
          t,
          height,
          row,
          fill: cellFill(row),
        });
      }
    }

    const gPlot = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    gPlot
      .selectAll('rect.cell')
      .data(cells)
      .enter()
      .append('rect')
      .attr('class', 'cell')
      .attr('x', (dataPoint) => xRange(dataPoint.t) ?? 0)
      .attr('y', (dataPoint) => yRange(dataPoint.height) ?? 0)
      .attr('width', bw)
      .attr('height', bh)
      .attr('fill', (dataPoint) => dataPoint.fill);

    const DENSITY_THRESHOLD = 15;
    const TIME_STEP = 3; // decimate to one third

    const timeIndex = new Map(times.map((t, i) => [t.getTime(), i]));

    const icons = gPlot
      .selectAll('g.icon')
      .data(
        cells.filter((dat) => {
          if (!dat.row) {
            return false;
          }
          const ti = timeIndex.get(dat.t.getTime()) ?? 0;

          return (
            ti % TIME_STEP === 0 &&
            Number.isFinite(dat.row.dens) &&
            (dat.row.dens as number) > DENSITY_THRESHOLD &&
            Number.isFinite(dat.row.ff) &&
            Number.isFinite(dat.row.dd)
          );
        }),
      )
      .enter()
      .append('g')
      .attr('class', 'icon')
      .attr('transform', (dat) => {
        const cx = (xRange(dat.t) ?? 0) + bw / 2;
        const cy = (yRange(dat.height) ?? 0) + bh / 2;
        const { dd } = dat.row!;
        const svgAngle = dd || 0;
        return `translate(${cx},${cy}) rotate(${svgAngle})`;
      });

    // eslint-disable-next-line func-names
    icons.each(function (dat) {
      const iconColor = pickIconColor(dat.fill);

      const gsel = select(this);
      const svgStr = pickWindSymbol(dat.row!.ff || 0);
      const { w: vw, h: vh } = getViewBoxWH(svgStr);

      const target = Math.min(bw, bh) * 2.9;
      const aspect = vw / vh;
      const width = target * aspect;
      const height = target;

      gsel.html(svgStr);
      const inner = gsel.select('svg');
      inner
        .attr('width', width)
        .attr('height', height)
        .attr('x', -width / 2)
        .attr('y', -height / 2)
        .attr('preserveAspectRatio', 'xMidYMid meet');

      inner.selectAll<SVGElement, unknown>('*').style('stroke', iconColor);
      // Use a <style> element for vector-effect and fill=none preservation
      inner
        .insert('style', ':first-child')
        .text(
          `* { vector-effect: non-scaling-stroke; } *[fill="none"] { fill: none; }`,
        );
    });

    // Tooltip
    const fmt2 = format('.2f');
    const fmt0 = format('.0f');
    const tf = utcFormat('%Y-%m-%d %H:%M');

    const tip = gPlot
      .append('g')
      .style('display', 'none')
      .attr('pointer-events', 'none');
    const tipBg = tip
      .append('rect')
      .attr('fill', 'white')
      .attr('stroke', '#94a3b8')
      .attr('rx', 4)
      .attr('ry', 4);

    const tipText = tip
      .append('text')
      .attr('font-size', TIP_FONT)
      .attr('fill', '#0f172a');

    const showTipAt = (
      event: PointerEvent,
      dat: { t: Date; h: number; row?: FlySafeRow },
    ): void => {
      const [mx, my] = pointer(event, gPlot.node() as SVGGElement);

      const content = dat.row
        ? `${tf(dat.row.datetime)} (UTC) - h=${fmt0(dat.row.height)} m, dens=${
            dat.row.dens && Number.isFinite(dat.row.dens)
              ? fmt2(dat.row.dens)
              : 'NA'
          } birds/km³, ff=${dat.row.ff && Number.isFinite(dat.row.ff) ? fmt2(dat.row.ff) : 'NA'} m/s, dd=${
            dat.row.dd && Number.isFinite(dat.row.dd) ? fmt0(dat.row.dd) : 'NA'
          }°`
        : `No data, h=${fmt0(dat.h)} m`;

      tipText.text(content);

      const bb = (tipText.node() as SVGTextElement).getBBox();
      tipBg
        .attr('x', bb.x - 6)
        .attr('y', bb.y - 4)
        .attr('width', bb.width + 12)
        .attr('height', bb.height + 8);

      const tipW = bb.width + 12;
      const tipH = bb.height + 8;

      // Above cursor, and clamp to plot bounds
      const xClamped = Math.max(
        4,
        Math.min(innerWidth - tipW - 4, mx - tipW / 2),
      );
      const yTarget = my - 10 - tipH;
      const yClamped = Math.max(4, Math.min(innerHeight - tipH - 4, yTarget));

      tip
        .style('display', null)
        .attr('transform', `translate(${xClamped},${yClamped})`)
        .raise();
    };

    const hideTip = (): Selection<SVGGElement, unknown, null, undefined> =>
      tip.style('display', 'none');

    // Cell pointer-events disabled; the time-select overlay handles them
    gPlot
      .selectAll<
        SVGRectElement,
        { t: Date; h: number; row?: FlySafeRow }
      >('rect.cell')
      .style('pointer-events', 'none');

    // Time-select overlay for click/drag to set map time
    const onTimeSelectRef = onTimeSelectRefStable;
    let dragging = false;
    let lastDispatchMs = 0;
    const THROTTLE_MS = 60;

    const dispatchTime = (event: PointerEvent): void => {
      const cb = onTimeSelectRef.current;
      if (!cb) {
        return;
      }
      const [mx] = pointer(event, gPlot.node() as SVGGElement);
      const hit = closestTimeBand(mx, times, (t) => xRange(t) ?? 0, bw);
      if (hit) {
        cb(hit);
      }
    };

    gPlot
      .append('rect')
      .attr('class', 'time-select-overlay')
      .attr('fill', 'transparent')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .style('pointer-events', 'all')
      .on('pointerdown', (event: PointerEvent) => {
        dragging = true;
        lastDispatchMs = 0;
        dispatchTime(event);
        (event.target as Element)?.setPointerCapture?.(event.pointerId);
      })
      .on('pointermove', (event: PointerEvent) => {
        // Show tooltip
        const [mx] = pointer(event, gPlot.node() as SVGGElement);
        const idx = times.findIndex(
          (t) => mx >= (xRange(t) ?? 0) && mx < (xRange(t) ?? 0) + bw,
        );
        if (idx >= 0) {
          const t = times[idx];
          const closestH = heights[0];
          const row = byKey.get(`${t.getTime()}|${closestH}`);
          showTipAt(event, { t, h: closestH, row }); // eslint-disable-line id-length
        }

        if (!dragging) {
          return;
        }
        const now = Date.now();
        if (now - lastDispatchMs < THROTTLE_MS) {
          return;
        }
        lastDispatchMs = now;
        dispatchTime(event);
      })
      .on('pointerup', () => {
        dragging = false;
      })
      .on('pointerleave', () => {
        dragging = false;
        hideTip();
      });

    const hourFormat = utcFormat('%H:%M');
    const dayFormat = utcFormat('%d %b');

    let xTickValues: Date[];

    if (!multiDay) {
      const xStep = Math.max(1, Math.ceil(times.length / 12));
      xTickValues = times.filter((_t, i) => i % xStep === 0);
    } else {
      xTickValues = times.filter((tickTime) => {
        const hour = tickTime.getUTCHours();
        const minute = tickTime.getUTCMinutes();
        return minute === 0 && (hour === 0 || hour === 12);
      });
    }

    const yStep = Math.max(1, Math.ceil(heights.length / 16));
    const yTickValues = heights.filter((_h, i) => i % yStep === 0);

    gPlot
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        axisBottom<Date>(xRange)
          .tickValues(xTickValues)
          .tickFormat((dt) => {
            let label;

            if (!multiDay) {
              label = hourFormat(dt);
            } else if (dt.getUTCHours() === 0) {
              label = dayFormat(dt);
            } else {
              label = '12:00';
            }

            const isLastTick =
              dt.getTime() === xTickValues[xTickValues.length - 1].getTime();

            return isLastTick ? `${label} UTC` : label;
          }),
      );

    const gy = gPlot
      .append('g')
      .attr('class', 'y-axis')
      .call(
        axisLeft<number>(yRange)
          .tickValues(yTickValues)
          .tickFormat((height) => `${height} m`),
      );

    // Conversion
    const metersToFeet = (mt: number): number => mt * 3.28084;
    const feetToMeters = (ft: number): number => ft / 3.28084;
    const maxHeightM = Math.max(...heights);
    const maxHeightFt = metersToFeet(maxHeightM);

    const feetTicks: number[] = [];

    for (let feetTick = 0; feetTick <= maxHeightFt; feetTick += FEET_STEP) {
      feetTicks.push(feetTick);
    }

    // Put foot conversion on the right
    const rightAxis = gPlot
      .append('g')
      .attr('class', 'y-axis-right')
      .attr('transform', `translate(${innerWidth},0)`);

    const rightTicks = rightAxis
      .selectAll('g.tick')
      .data(feetTicks)
      .enter()
      .append('g')
      .attr('class', 'tick')
      .attr('transform', (ft) => {
        const hMeters = feetToMeters(ft);
        return `translate(0,${yLinear(hMeters)})`;
      });

    rightTicks
      .append('line')
      .attr('x1', 0)
      .attr('x2', 6)
      .attr('y1', 0)
      .attr('y2', 0)
      .attr('stroke', '#64748b');

    rightTicks
      .append('text')
      .attr('x', 8)
      .attr('y', 0)
      .attr('dy', '0.32em')
      .style('font-size', `${TICK_FONT}px`)
      .attr('text-anchor', 'start')
      .text((ft) => `${ft} ft`);

    gy.selectAll('text').style('font-size', `${TICK_FONT}px`);

    // Vertical line for current map time
    if (mapTime && !isNaN(mapTime.getTime()) && times.length > 0) {
      const mapMs = mapTime.getTime();
      // Find closest time band
      let closest = times[0];
      let closestDist = Math.abs(closest.getTime() - mapMs);
      for (const t of times) {
        const dist = Math.abs(t.getTime() - mapMs);
        if (dist < closestDist) {
          closest = t;
          closestDist = dist;
        }
      }
      const xPos = (xRange(closest) ?? 0) + bw / 2;
      gPlot
        .append('line')
        .attr('class', 'map-time-line')
        .attr('x1', xPos)
        .attr('x2', xPos)
        .attr('y1', 0)
        .attr('y2', innerHeight)
        .attr('stroke', '#ff0000')
        .attr('stroke-width', 2)
        .attr('pointer-events', 'none');
    }

    // At end, store refs:
    chartRef.current = { gPlot, xRange, bw, times, innerHeight };
    return (): void => {
      cleanup(node);
      chartRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerWidth, mode, rows, timeWindow]);

  // Indicator line effect — lightweight, runs on every tick
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !mapTime || isNaN(mapTime.getTime())) {
      return;
    }
    const { gPlot, xRange, bw, times, innerHeight } = chart;

    // Remove old line
    gPlot.selectAll('.map-time-line').remove();

    if (times.length === 0) {
      return;
    }

    // Find closest time band
    const mapMs = mapTime.getTime();
    let closest = times[0];
    let closestDist = Math.abs(closest.getTime() - mapMs);
    for (const t of times) {
      const dist = Math.abs(t.getTime() - mapMs);
      if (dist < closestDist) {
        closest = t;
        closestDist = dist;
      }
    }

    const xPos = (xRange(closest) ?? 0) + bw / 2;
    gPlot
      .append('line')
      .attr('class', 'map-time-line')
      .attr('x1', xPos)
      .attr('x2', xPos)
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', '#ff0000')
      .attr('stroke-width', 2)
      .attr('pointer-events', 'none');
  }, [mapTime]);

  const titleTooltip = (
    <>
      <strong>{t('flysafe-tooltip-vpts-title')}</strong>{' '}
      {t('flysafe-tooltip-vpts-desc')}
      <br />
      {t('flysafe-tooltip-vpts-wind')}
    </>
  );

  const stationName = stationId ? t(`stations.${stationId}`) : '';

  return (
    <div style={{ width: '100%' }}>
      <Tooltip title={titleTooltip}>
        <Typography color="text.secondary" variant="body1">
          {t('flysafe-heat-map')}
          {stationName ? ` - ${stationName}` : ''}
        </Typography>
      </Tooltip>
      <div
        ref={containerRef}
        style={{
          width: '100%',
          minWidth: 0,
        }}
      >
        <div
          ref={svgHostRef}
          style={{
            width: '100%',
          }}
        />
      </div>
    </div>
  );
};
export default HeatMap;
